/**
 * ConfirmationStep - Final review before saving Key Locations
 *
 * Features:
 * - Summary cards (meeting point, waypoints, route stats)
 * - Map preview with route line
 * - Elevation profile (if applicable)
 * - Final review before save
 * - Option to go back and edit
 *
 * Epic: MVP 1.20.6-S - Key Locations Guided Editor with GPX Intelligence
 * Story: SCRUM-239 (US-1.20.6-S.5-02: Confirmation Step Component)
 *
 * @component
 */

import React, { useState, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import Map, { Marker, Source, Layer, NavigationControl } from 'react-map-gl';
import { MapPin, Flag as _Flag } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';

/**
 * ConfirmationStep
 *
 * Final step showing complete summary of key locations data.
 */
const ConfirmationStep = memo(function ConfirmationStep({
  meetingPoint,
  waypoints = [],
  routeStats,
  elevationData,
  categoryL1Id: _categoryL1Id
}) {
  // Category labels
  const meetingPointLabels = {
    39: 'Trailhead',
    40: 'Base Camp',
    41: 'Start Point',
    42: 'Put-In'
  };

  const meetingPointLabel = meetingPointLabels[_categoryL1Id] || 'Meeting Point';

  const mapboxToken = import.meta.env.VITE_MAPBOX_API_KEY;

  // Calculate map bounds to fit all points
  const allPoints = useMemo(() => {
    const points = [];
    if (meetingPoint && meetingPoint.latitude && meetingPoint.longitude) {
      points.push({ lat: meetingPoint.latitude, lon: meetingPoint.longitude });
    }
    waypoints.forEach(wp => {
      if (wp.latitude && wp.longitude) {
        points.push({ lat: wp.latitude, lon: wp.longitude });
      }
    });
    return points;
  }, [meetingPoint, waypoints]);

  // Calculate center and zoom level
  const { center, zoom } = useMemo(() => {
    if (allPoints.length === 0) {
      return { center: { lat: 39.8283, lon: -98.5795 }, zoom: 4 };
    }

    if (allPoints.length === 1) {
      return { center: { lat: allPoints[0].lat, lon: allPoints[0].lon }, zoom: 12 };
    }

    // Calculate bounds
    const lats = allPoints.map(p => p.lat);
    const lons = allPoints.map(p => p.lon);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);

    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;

    // Estimate zoom level based on lat/lon span
    const latSpan = maxLat - minLat;
    const lonSpan = maxLon - minLon;
    const maxSpan = Math.max(latSpan, lonSpan);

    let estimatedZoom = 10;
    if (maxSpan < 0.01) {estimatedZoom = 14;}
    else if (maxSpan < 0.05) {estimatedZoom = 12;}
    else if (maxSpan < 0.1) {estimatedZoom = 11;}
    else if (maxSpan < 0.5) {estimatedZoom = 9;}
    else if (maxSpan < 1) {estimatedZoom = 8;}
    else if (maxSpan < 5) {estimatedZoom = 6;}

    return { center: { lat: centerLat, lon: centerLon }, zoom: estimatedZoom };
  }, [allPoints]);

  // Map viewport state
  const [viewState, setViewState] = useState({
    latitude: center.lat,
    longitude: center.lon,
    zoom: zoom
  });

  // Update viewport when points change
  useEffect(() => {
    setViewState({
      latitude: center.lat,
      longitude: center.lon,
      zoom: zoom
    });
  }, [center.lat, center.lon, zoom]);

  // Create GeoJSON for route line
  const routeGeoJSON = useMemo(() => {
    if (allPoints.length < 2) {return null;}

    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: allPoints.map(p => [p.lon, p.lat])
      }
    };
  }, [allPoints]);

  const hasValidMapData = allPoints.length > 0 && mapboxToken;

  return (
    <div className="confirmation-step space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Review Key Locations
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Review your key locations data before saving. You can go back to make changes.
        </p>
      </div>

      {/* Meeting Point Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <svg
              className="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>

          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 mb-1">{meetingPointLabel}</h4>
            <p className="text-sm text-gray-700 font-medium">{meetingPoint.name}</p>

            {meetingPoint.address && (
              <p className="text-sm text-gray-600 mt-1">{meetingPoint.address}</p>
            )}

            {meetingPoint.latitude && meetingPoint.longitude && (
              <p className="text-xs text-gray-500 mt-2">
                {meetingPoint.latitude.toFixed(4)}, {meetingPoint.longitude.toFixed(4)}
              </p>
            )}

            {meetingPoint.description && (
              <p className="text-sm text-gray-600 mt-2 italic">&quot;{meetingPoint.description}&quot;</p>
            )}

            {/* Amenities */}
            {meetingPoint.amenities && meetingPoint.amenities.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {meetingPoint.amenities.map((amenity, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded"
                  >
                    {amenity.icon && <span>{amenity.icon}</span>}
                    {amenity.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Waypoints Summary */}
      {waypoints.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                />
              </svg>
            </div>

            <div className="flex-1">
              <h4 className="font-semibold text-gray-900">
                Waypoints ({waypoints.length})
              </h4>
            </div>
          </div>

          <div className="space-y-2 ml-11">
            {waypoints.map((waypoint, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                <span className="w-6 h-6 flex items-center justify-center bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                  {idx + 1}
                </span>
                <div className="flex-1">
                  <span className="font-medium text-gray-900">{waypoint.name}</span>
                  {waypoint.mile_marker !== null && waypoint.mile_marker !== undefined && (
                    <span className="ml-2 text-gray-500">
                      @ {waypoint.mile_marker} mi
                    </span>
                  )}
                </div>
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                  {waypoint.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Route Stats Summary */}
      {routeStats && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>

            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-3">Route Statistics</h4>

              <div className="grid grid-cols-3 gap-4">
                {routeStats.distance_miles && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Distance</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {routeStats.distance_miles} mi
                    </p>
                  </div>
                )}

                {routeStats.elevation_gain_ft && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Elevation Gain</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {routeStats.elevation_gain_ft} ft
                    </p>
                  </div>
                )}

                {routeStats.route_type && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Route Type</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {routeStats.route_type.replace(/_/g, ' ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Elevation Profile */}
      {elevationData && elevationData.points && elevationData.points.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h4 className="font-semibold text-gray-900 mb-3">Elevation Profile</h4>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <svg
              className="w-12 h-12 mx-auto text-gray-400 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
              />
            </svg>
            <p className="text-sm text-gray-500">
              Elevation profile chart will be rendered here
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {elevationData.points.length} elevation points
            </p>
          </div>
        </div>
      )}

      {/* Interactive Map with Route and Waypoints */}
      {hasValidMapData && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Route Map Preview
          </label>
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow-sm" style={{ height: '500px' }}>
            <Map
              {...viewState}
              onMove={(evt) => setViewState(evt.viewState)}
              mapboxAccessToken={mapboxToken}
              mapStyle="mapbox://styles/mapbox/outdoors-v12"
              style={{ width: '100%', height: '100%' }}
            >
              <NavigationControl position="top-right" />

              {/* Route Line */}
              {routeGeoJSON && (
                <Source id="route" type="geojson" data={routeGeoJSON}>
                  <Layer
                    id="route-line"
                    type="line"
                    paint={{
                      'line-color': '#3b82f6',
                      'line-width': 3,
                      'line-opacity': 0.8
                    }}
                  />
                </Source>
              )}

              {/* Meeting Point Marker */}
              {meetingPoint && meetingPoint.latitude && meetingPoint.longitude && (
                <Marker
                  latitude={meetingPoint.latitude}
                  longitude={meetingPoint.longitude}
                >
                  <div className="relative">
                    <MapPin
                      className="w-8 h-8 text-rose-600"
                      fill="currentColor"
                      strokeWidth={1.5}
                      style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                      }}
                    />
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-white border border-gray-300 rounded shadow-md whitespace-nowrap text-xs font-medium">
                      {meetingPoint.name || meetingPointLabel}
                    </div>
                  </div>
                </Marker>
              )}

              {/* Waypoint Markers */}
              {waypoints.map((waypoint, index) => {
                if (!waypoint.latitude || !waypoint.longitude) {return null;}

                return (
                  <Marker
                    key={waypoint.id || index}
                    latitude={waypoint.latitude}
                    longitude={waypoint.longitude}
                  >
                    <div className="relative">
                      <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-lg border-2 border-white">
                        {index + 1}
                      </div>
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-white border border-gray-300 rounded shadow-md whitespace-nowrap text-xs font-medium">
                        {waypoint.name}
                      </div>
                    </div>
                  </Marker>
                );
              })}
            </Map>
          </div>
          <p className="text-xs text-gray-500 italic">
            {meetingPoint && meetingPoint.latitude && '1 meeting point'}{' '}
            {waypoints.length > 0 && `• ${waypoints.length} waypoint${waypoints.length !== 1 ? 's' : ''}`}
            {routeGeoJSON && ' • Route line shown'}
          </p>
        </div>
      )}

      {!mapboxToken && allPoints.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            Map preview unavailable: Mapbox API key not configured
          </p>
        </div>
      )}

      {allPoints.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            No map data available - add a meeting point with coordinates to see the map
          </p>
        </div>
      )}

      {/* Final Check */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <svg
          className="w-5 h-5 text-blue-600 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium text-blue-900">
            Ready to save?
          </p>
          <p className="text-xs text-blue-700 mt-1">
            Click &quot;Save Key Locations&quot; to finalize. You can always edit this later.
          </p>
        </div>
      </div>
    </div>
  );
});

ConfirmationStep.propTypes = {
  /**
   * Meeting point data
   */
  meetingPoint: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    description: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    amenities: PropTypes.array
  }).isRequired,

  /**
   * Waypoints array
   */
  waypoints: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      mile_marker: PropTypes.number
    })
  ),

  /**
   * Route statistics
   */
  routeStats: PropTypes.shape({
    distance_miles: PropTypes.number,
    elevation_gain_ft: PropTypes.number,
    route_type: PropTypes.string
  }),

  /**
   * Elevation profile data
   */
  elevationData: PropTypes.shape({
    points: PropTypes.array,
    stats: PropTypes.object
  }),

  /**
   * L1 Category ID (for label customization)
   */
  categoryL1Id: PropTypes.number.isRequired
};

export default ConfirmationStep;
